import React, { useState, useEffect } from 'react';
import { Save, Trash2, Plus, ArrowLeft, Radio, Lock, Eye, EyeOff, Instagram, Music, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const Admin = () => {
  const [shows, setShows] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingShow, setEditingShow] = useState(null);
  const [editingCredit, setEditingCredit] = useState(null);
  const [activeTab, setActiveTab] = useState('shows');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const { toast } = useToast();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Check if already authenticated
  useEffect(() => {
    const authToken = sessionStorage.getItem('wvod_admin_auth');
    if (authToken === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('wvod_admin_auth', 'authenticated');
        toast({ title: "Welcome!", description: "Login successful" });
      } else {
        setLoginError('Incorrect password');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('wvod_admin_auth');
    toast({ title: "Logged out", description: "See you next time!" });
  };

  // Fetch shows on load
  useEffect(() => {
    if (isAuthenticated) {
      fetchShows();
      fetchCredits();
    }
  }, [isAuthenticated]);

  const fetchShows = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shows`);
      const data = await response.json();
      setShows(data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load shows", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/attribution`);
      const data = await response.json();
      setCredits(data);
    } catch (error) {
      console.log('Failed to load credits');
    }
  };

  const updateShow = async (showId, updatedData) => {
    try {
      const response = await fetch(`${API_URL}/api/shows/${showId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });
      
      if (response.ok) {
        toast({ title: "Success", description: "Show updated successfully" });
        fetchShows();
        setEditingShow(null);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update show", variant: "destructive" });
    }
  };

  const addShow = async () => {
    try {
      const newShow = {
        show: "New Show",
        time: "12:00 PM",
        status: "Later",
        desc: "Description",
        order: shows.length
      };
      
      const response = await fetch(`${API_URL}/api/shows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newShow)
      });
      
      if (response.ok) {
        toast({ title: "Success", description: "New show added" });
        fetchShows();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to add show", variant: "destructive" });
    }
  };

  const deleteShow = async (showId) => {
    if (!window.confirm("Are you sure you want to delete this show?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/shows/${showId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast({ title: "Success", description: "Show deleted" });
        fetchShows();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete show", variant: "destructive" });
    }
  };

  // Credit management functions
  const addCredit = async () => {
    try {
      const newCredit = { platform: "IG", handle: "@newhandle", order: credits.length };
      const response = await fetch(`${API_URL}/api/attribution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCredit)
      });
      if (response.ok) {
        toast({ title: "Success", description: "Credit added" });
        fetchCredits();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to add credit", variant: "destructive" });
    }
  };

  const updateCredit = async (creditId, updatedData) => {
    try {
      const response = await fetch(`${API_URL}/api/attribution/${creditId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });
      if (response.ok) {
        toast({ title: "Success", description: "Credit updated" });
        fetchCredits();
        setEditingCredit(null);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update credit", variant: "destructive" });
    }
  };

  const deleteCredit = async (creditId) => {
    if (!window.confirm("Delete this credit?")) return;
    try {
      const response = await fetch(`${API_URL}/api/attribution/${creditId}`, { method: 'DELETE' });
      if (response.ok) {
        toast({ title: "Success", description: "Credit deleted" });
        fetchCredits();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete credit", variant: "destructive" });
    }
  };

  // Credit Card Component
  const CreditCard = ({ credit }) => {
    const [formData, setFormData] = useState({
      platform: credit.platform,
      handle: credit.handle,
      order: credit.order
    });
    const isEditing = editingCredit === credit.id;

    return (
      <Card className={`p-4 ${isEditing ? 'border-2 border-purple-500 bg-purple-500/10' : 'bg-white/5 border-white/10'}`}>
        {isEditing ? (
          <div className="space-y-3">
            <select 
              value={formData.platform}
              onChange={(e) => setFormData({...formData, platform: e.target.value})}
              className="w-full p-2 rounded-md bg-black/30 border border-white/20 text-white"
            >
              <option value="IG">Instagram</option>
              <option value="TikTok">TikTok</option>
              <option value="YouTube">YouTube</option>
            </select>
            <Input 
              value={formData.handle}
              onChange={(e) => setFormData({...formData, handle: e.target.value})}
              placeholder="@handle or name"
              className="bg-black/30 border-white/20 text-white"
            />
            <Input 
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
              placeholder="Order"
              className="bg-black/30 border-white/20 text-white"
            />
            <div className="flex gap-2">
              <Button onClick={() => updateCredit(credit.id, formData)} className="bg-green-500 hover:bg-green-600 flex-1">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button onClick={() => setEditingCredit(null)} variant="outline" className="border-white/20">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {credit.platform === 'IG' && <Instagram className="w-5 h-5 text-pink-400" />}
              {credit.platform === 'TikTok' && <Music className="w-5 h-5 text-cyan-400" />}
              {credit.platform === 'YouTube' && <Youtube className="w-5 h-5 text-red-400" />}
              <div>
                <p className="font-bold text-white">{credit.handle}</p>
                <p className="text-xs text-gray-400">{credit.platform} • Order: {credit.order}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setEditingCredit(credit.id)} size="sm" className="bg-purple-500 hover:bg-purple-600">Edit</Button>
              <Button onClick={() => deleteCredit(credit.id)} size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/20">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const ShowCard = ({ show }) => {
    const [formData, setFormData] = useState({
      show: show.show,
      time: show.time,
      status: show.status,
      desc: show.desc,
      order: show.order
    });
    
    const isEditing = editingShow === show.id;

    const handleSave = () => {
      updateShow(show.id, formData);
    };

    return (
      <Card className={`p-6 ${isEditing ? 'border-2 border-purple-500 bg-purple-500/10' : 'bg-white/5 border-white/10'}`}>
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Show Name</label>
              <Input 
                value={formData.show}
                onChange={(e) => setFormData({...formData, show: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Time</label>
              <Input 
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Status</label>
              <select 
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full p-2 rounded-md bg-black/30 border border-white/20 text-white"
              >
                <option value="On Air">On Air</option>
                <option value="Up Next">Up Next</option>
                <option value="Later">Later</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Description</label>
              <Input 
                value={formData.desc}
                onChange={(e) => setFormData({...formData, desc: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Order (0 = first)</label>
              <Input 
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                className="bg-black/30 border-white/20 text-white"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} className="bg-green-500 hover:bg-green-600 flex-1">
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button onClick={() => setEditingShow(null)} variant="outline" className="border-white/20">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase mb-2 ${
                  show.status === 'On Air' ? 'bg-purple-500 text-white' :
                  show.status === 'Up Next' ? 'bg-pink-500 text-white' :
                  'bg-gray-500 text-white'
                }`}>
                  {show.status}
                </span>
                <h3 className="text-xl font-bold text-white">{show.show}</h3>
                <p className="text-purple-400 font-mono">{show.time}</p>
                <p className="text-gray-400 text-sm mt-1">{show.desc}</p>
              </div>
              <span className="text-gray-500 text-sm">#{show.order}</span>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setEditingShow(show.id)} className="bg-purple-500 hover:bg-purple-600 flex-1">
                Edit
              </Button>
              <Button onClick={() => deleteShow(show.id)} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/20">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-white/5 border-white/10 backdrop-blur-lg">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold">WVOD Admin</h1>
            <p className="text-gray-400 mt-2">Enter password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Input 
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-black/30 border-white/20 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {loginError && (
              <p className="text-red-400 text-sm text-center">{loginError}</p>
            )}
            
            <Button type="submit" className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90">
              Login
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/design3" className="text-gray-400 hover:text-purple-400 text-sm">
              ← Back to site
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <a href="/design3" className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">WVOD Admin</h1>
                <p className="text-sm text-gray-400">Manage Content</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleLogout} variant="outline" className="border-white/20 hover:bg-white/10">
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button 
            onClick={() => setActiveTab('shows')}
            className={activeTab === 'shows' ? 'bg-purple-500' : 'bg-white/10 hover:bg-white/20'}
          >
            Featured Shows
          </Button>
          <Button 
            onClick={() => setActiveTab('credits')}
            className={activeTab === 'credits' ? 'bg-purple-500' : 'bg-white/10 hover:bg-white/20'}
          >
            Content Attribution
          </Button>
        </div>

        {/* Shows Tab */}
        {activeTab === 'shows' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Featured Shows</h2>
              <Button onClick={addShow} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90">
                <Plus className="w-4 h-4 mr-2" /> Add Show
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Loading shows...</p>
              </div>
            ) : shows.length === 0 ? (
              <Card className="p-12 text-center bg-white/5 border-white/10">
                <p className="text-gray-400 mb-4">No shows found</p>
                <Button onClick={addShow} className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Show
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4">
                {shows.sort((a, b) => a.order - b.order).map((show) => (
                  <ShowCard key={show.id} show={show} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Credits Tab */}
        {activeTab === 'credits' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Content Attribution Credits</h2>
              <Button onClick={addCredit} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90">
                <Plus className="w-4 h-4 mr-2" /> Add Credit
              </Button>
            </div>
            {credits.length === 0 ? (
              <Card className="p-12 text-center bg-white/5 border-white/10">
                <p className="text-gray-400 mb-4">No credits found</p>
                <Button onClick={addCredit} className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Credit
                </Button>
              </Card>
            ) : (
              <div className="grid gap-3">
                {credits.sort((a, b) => a.order - b.order).map((credit) => (
                  <CreditCard key={credit.id} credit={credit} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Changes are saved instantly and appear on your site immediately.</p>
          <p className="mt-2">No redeployment needed!</p>
        </div>
      </div>
    </div>
  );
};

export default Admin;
